import type { MembraneClient } from "@membranehq/sdk";

import { open } from "@/utils/open";
import { optionalStringProperty, property } from "@/utils/unknown";

/**
 * Normalizes a variable reference from Membrane parameter schemas.
 * Supports two formats:
 * - "workspace_id" -> "workspace_id"
 * - "$.workspace_id" -> "workspace_id"
 *
 * See https://routine-company.slack.com/archives/C08EGE3TAQ2/p1772102602243169 for a bit more context on the 2 syntaxes.
 *
 * @param varRef - The variable reference string
 * @returns The normalized parameter key
 */
export function normalizeVarReference(varRef: string): string {
  if (varRef.startsWith("$.")) {
    return varRef.slice(2);
  }
  return varRef;
}

// ── Browser-based connection flow (connection requests) ─────────────────────

/**
 * Branded landing page Membrane sends the user's browser to once an
 * interactive connection flow completes (Membrane appends `connectionId=...`
 * on success or `error=...` on failure). The page confirms the outcome and
 * bounces the user back into the app at the in-app path given in its `path`
 * query parameter.
 */
const AUTHENTICATED_URI = "https://routine.co/_membrane/oauth/authenticated";

/**
 * Page on routine.co that forwards a connection payload to Membrane's
 * `/connect` endpoint as a form POST. `/connect` rejects GET requests, so the
 * browser cannot be pointed at it directly; the forwarder auto-submits the
 * `payload` query parameter and the user lands straight on the provider's
 * authorization page.
 */
const CONNECT_FORWARDER_URI = "https://routine.co/_membrane/oauth/connect";

const CONNECTION_REQUEST_POLL_INTERVAL_MS = 2_000;

/**
 * Give up on abandoned flows after 10 minutes — the same budget the previous
 * popup-based flow used before destroying its hidden popup.
 */
const CONNECTION_REQUEST_TIMEOUT_MS = 10 * 60 * 1_000;

type PendingConnectionRequest = { requestId: string; url: string };

function parsePendingConnectionRequest(
  value: unknown
): PendingConnectionRequest {
  const requestId = optionalStringProperty(value, "requestId");
  const url = optionalStringProperty(value, "url");
  if (!requestId || !url) {
    throw new Error("Malformed connection request response from Membrane");
  }
  return { requestId, url };
}

type ConnectionRequestOutcome =
  | { status: "pending" }
  | { status: "success"; connectionId: string }
  | { status: "error"; message: string }
  | { status: "cancelled" };

function parseConnectionRequestOutcome(
  value: unknown
): ConnectionRequestOutcome {
  const status = optionalStringProperty(value, "status");
  switch (status) {
    case "pending":
      return { status: "pending" };
    case "success": {
      const connectionId = optionalStringProperty(value, "resultConnectionId");
      if (!connectionId) {
        throw new Error(
          "Membrane connection request succeeded without a connection ID"
        );
      }
      return { status: "success", connectionId };
    }
    case "error":
      return {
        status: "error",
        message:
          optionalStringProperty(property(value, "resultError"), "message") ??
          "Unknown connection error",
      };
    case "cancelled":
      return { status: "cancelled" };
    default:
      throw new Error(
        `Unknown Membrane connection request status: ${String(status)}`
      );
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs an interactive connection flow (OAuth, or OAuth preceded by input
 * collection) in the browser — the default browser on desktop, a new tab on
 * web — instead of the SDK's popup flow.
 *
 * This creates a Membrane connection request (used purely to track the flow's
 * outcome), then opens routine.co's connect forwarder, which POSTs the
 * payload to Membrane's `/connect` endpoint (see "Connection UI Without the
 * Front-End SDK" in the Membrane docs). That sends the user straight into the
 * provider's OAuth flow, like the SDK's popup used to — unlike the connection
 * request's hosted screen (`request.url`), which inserts a "Connect to X"
 * confirmation card. The `connectionRequestId` field ties the flow to the
 * request we poll, and `redirectUri` brings the user back into the app once
 * the flow completes.
 *
 * Resolves with the connection on success, `null` when the user cancels or
 * abandons the flow, and rejects when the connection attempt fails.
 */
export async function connectInBrowser(options: {
  client: Pick<MembraneClient, "get" | "post" | "getToken">;
  integrationId: string;
  /** Reconnect this existing connection instead of creating a new one. */
  connectionId?: string;
  /** Auth option to use, for integrations that support several. */
  authOptionKey?: string;
  /** Connection input fields (e.g. a subdomain) collected before the flow. */
  input?: Record<string, string>;
  /** In-app path to bring the user back to once the flow completes. */
  redirectPath: string;
}): Promise<{ id: string } | null> {
  const { client, integrationId, connectionId, authOptionKey, input } = options;
  const redirectUri = `${AUTHENTICATED_URI}?path=${options.redirectPath}`;
  const request = parsePendingConnectionRequest(
    await client.post("/connection-requests", {
      integrationId,
      redirectUri,
      ...(connectionId ? { connectionId } : { allowMultipleConnections: true }),
    })
  );

  const payload = {
    token: await client.getToken(),
    connectionRequestId: request.requestId,
    redirectUri,
    integrationId,
    ...(connectionId ? { connectionId } : { allowMultipleConnections: true }),
    ...(authOptionKey !== undefined ? { authOptionKey } : {}),
    ...(input !== undefined && Object.keys(input).length > 0 ? { input } : {}),
  };
  void open(
    `${CONNECT_FORWARDER_URI}?payload=${encodeURIComponent(
      JSON.stringify(payload)
    )}`
  );

  const maxPollAttempts = Math.ceil(
    CONNECTION_REQUEST_TIMEOUT_MS / CONNECTION_REQUEST_POLL_INTERVAL_MS
  );
  for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
    await sleep(CONNECTION_REQUEST_POLL_INTERVAL_MS);
    const outcome = parseConnectionRequestOutcome(
      await client.get(`/connection-requests/${request.requestId}`)
    );
    if (outcome.status === "pending") continue;
    if (outcome.status === "cancelled") return null;
    if (outcome.status === "error") throw new Error(outcome.message);
    return { id: outcome.connectionId };
  }
  // Abandoned flow: mirror the SDK's "popup closed" resolution.
  return null;
}
